"use client"

import type React from "react"
import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react"
import { toast } from "@/hooks/use-toast"
import { useDispatch, useSelector } from "react-redux"
import { AppDispatch, RootState } from "@/store"
import { logoutSession, verifySession } from "@/store/auth/authThunks"
import { auth } from "@/lib/firebase"
import { onAuthStateChanged } from "firebase/auth"
import { setUser, setInitialized } from "@/store/auth/authSlice"
import { User } from "@/types"

interface AdminAuthContextType {
  user: User | null
  roles: string[]
  loading: boolean
  logout: () => Promise<void>
  katexLoaded: boolean
}

const AdminAuthContext = createContext<AdminAuthContextType | undefined>(undefined)

export function useAdminAuth() {
  const context = useContext(AdminAuthContext)
  if (context === undefined) {
    throw new Error("useAdminAuth  must be used within an AdminAuthProvider")
  }
  return context
}

export function AdminAuthProvider({ children }: { children: React.ReactNode }) {
  const [katexLoaded, setKatexLoaded] = useState(false)
  const { user, roles, loading, initialized } = useSelector((state: RootState) => state.auth)
  const dispatch = useDispatch<AppDispatch>()

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        await dispatch(verifySession())
      } else {
        dispatch(setUser({ user: null }))
      }
      dispatch(setInitialized(true))
    })

    return () => unsubscribe()
  }, [dispatch])

  const logout = useCallback(async () => {
    dispatch(logoutSession())

    toast({
      title: "Logged out",
      description: "You have been logged out successfully.",
    })

    // Redirect to parent app
    setTimeout(() => {
      window.location.href = `https://${import.meta.env.VITE_ENV === 'uat' ? 'dev.' : ''}pilotpariksha.com/dashboard`
    }, 1000)
  }, [dispatch])


  // Load KaTeX dynamically
  useEffect(() => {
    const loadKatex = async () => {
      if (typeof window !== "undefined" && !(window as any).katex) {
        // Load KaTeX CSS
        const link = document.createElement("link")
        link.rel = "stylesheet"
        link.href = "https://cdn.jsdelivr.net/npm/katex@0.16.8/dist/katex.min.css"
        document.head.appendChild(link)

        // Load KaTeX JS
        const script = document.createElement("script")
        script.src = "https://cdn.jsdelivr.net/npm/katex@0.16.8/dist/katex.min.js"
        script.onload = () => {
          setKatexLoaded(true)
        }
        document.head.appendChild(script)
      } else if ((window as any).katex) {
        setKatexLoaded(true)
      }
    }

    loadKatex()
  }, [])

  const value = useMemo(
    () => ({
      user,
      roles,
      loading: loading || !initialized,
      logout,
      katexLoaded,
    }),
    [user, roles, loading, initialized, logout, katexLoaded]
  )

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>
}
